import { useMemo } from 'react'
import { cn } from '@/lib/cn'
import type { AuditLog } from '@/types'

export interface AuditLogStatsProps {
  total: number
  logs: AuditLog[]
  isLoading: boolean
}

interface StatCard {
  label: string
  value: number
  borderColor: string
}

export function AuditLogStats({
  total,
  logs,
  isLoading,
}: AuditLogStatsProps): React.ReactElement {
  const stats = useMemo<StatCard[]>(() => {
    const creates = logs.filter((l) => l.action === 'create').length
    const updates = logs.filter((l) => l.action === 'update').length
    const deletes = logs.filter((l) => l.action === 'delete').length
    const financial = logs.filter((l) => l.isFinancial).length

    return [
      { label: 'Total Logs', value: total, borderColor: 'border-l-gray-400 dark:border-l-gray-500' },
      { label: 'Creates', value: creates, borderColor: 'border-l-green-500 dark:border-l-green-400' },
      { label: 'Updates', value: updates, borderColor: 'border-l-amber-500 dark:border-l-amber-400' },
      { label: 'Deletes', value: deletes, borderColor: 'border-l-red-500 dark:border-l-red-400' },
      { label: 'Financial', value: financial, borderColor: 'border-l-blue-500 dark:border-l-blue-400' },
    ]
  }, [total, logs])

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="rounded-lg border border-l-4 border-gray-200 border-l-gray-300 bg-white px-4 py-3 dark:border-gray-700 dark:border-l-gray-600 dark:bg-gray-800"
          >
            <div className="mb-2 h-3 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-7 w-10 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className={cn(
            'rounded-lg border border-l-4 border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800',
            stat.borderColor,
          )}
        >
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {stat.label}
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
            {stat.value}
          </p>
        </div>
      ))}
    </div>
  )
}

AuditLogStats.displayName = 'AuditLogStats'
